import {
  LabwareSource,
  StoredLabware,
} from '@automata/wfb-api/src/apis/wfbApiSchemas';

import { TaskType } from '../../types';

export const getLabwareSources = (
  task: TaskType
): Record<string, LabwareSource | StoredLabware> | undefined => {
  if (
    'labware_sources' in task &&
    task.labware_sources &&
    Object.keys(task.labware_sources).length > 0
  ) {
    return task.labware_sources;
  }

  if (!('tasks' in task)) return undefined;

  const subTasks = {
    ...task.tasks,
    ...('next' in task && task.next ? task.next.tasks : {}),
  };

  for (const subTask of Object.values(subTasks)) {
    const labwareSources = getLabwareSources(subTask);
    if (labwareSources) return labwareSources;
  }

  return undefined;
};

export const isTaskInConditional = (
  taskId: string,
  conditionalTask: TaskType
): boolean => {
  if (!('tasks' in conditionalTask)) return false;

  // Check if task is in the main 'tasks' object
  if (taskId in conditionalTask.tasks) return true;

  // Check if task is in the 'next.tasks' object (else branch)
  if (
    'next' in conditionalTask &&
    conditionalTask.next &&
    'tasks' in conditionalTask.next
  ) {
    if (taskId in conditionalTask.next.tasks) return true;
  }

  return false;
};

export const getSourceTaskIds = (
  labwareSource: LabwareSource | StoredLabware,
  parentTask?: TaskType
): string[] | null => {
  // First check if there's an explicit source_task
  if ('source_task' in labwareSource && labwareSource.source_task) {
    if (typeof labwareSource.source_task === 'string') {
      const explicitSourceTaskId = labwareSource.source_task;

      // If we have a parent (conditional), check if the explicit source is within the same conditional
      if (parentTask) {
        const isSourceTaskInSameConditional = isTaskInConditional(
          explicitSourceTaskId,
          parentTask
        );

        // If the source task is within the same conditional, use it for intra-branch connections
        if (isSourceTaskInSameConditional) {
          return [explicitSourceTaskId];
        }

        // If the source task is outside the conditional, use the conditional parent instead
        return [parentTask.id];
      }

      // No parent, use the explicit source
      return [explicitSourceTaskId];
    } else {
      // handle ConditionalSourceTask type

      return labwareSource.source_task.task_ids;
    }
  }

  // If no explicit source_task and we have a parent (conditional), use the parent
  if (parentTask) {
    return [parentTask.id];
  }

  return null;
};

export const getConditionalBranchIndex = (
  targetTask: TaskType,
  parentTask?: TaskType
): number => {
  const isConditionalSubtask =
    parentTask && 'tasks' in parentTask && 'next' in parentTask;

  // Default to if branch for non-conditional parents
  if (!isConditionalSubtask) return 0;

  // If branch is contained in tasks, Else branch in next.tasks
  const isIfBranch = Object.values(parentTask.tasks).some(
    (t) => t.id === targetTask.id
  );

  // if branch index is 0, else branch index is 1
  return isIfBranch ? 0 : 1;
};

export const shouldCreateTaskToTaskConnection = (
  labwareSource: LabwareSource | StoredLabware,
  parentTask?: TaskType
): boolean => {
  const hasExplicitSourceTask =
    labwareSource.type === 'labware_source' &&
    Boolean(labwareSource.source_task);
  return hasExplicitSourceTask || Boolean(parentTask);
};

export const getSourceHandleForTask = (
  sourceTask: TaskType | undefined,
  labwareId: string,
  handlePairIdOrderByNode: Record<string, string[]>,
  targetTask: TaskType,
  parentTask?: TaskType
): { baseSourceHandleId: string | undefined; sourceHandleFormat: string } => {
  if (!sourceTask) {
    return { baseSourceHandleId: undefined, sourceHandleFormat: '' };
  }

  const sourceTaskId = sourceTask.id;
  const isSourceConditional =
    'type' in sourceTask && sourceTask.type === 'conditional';

  let baseSourceHandleId: string | undefined;

  if (isSourceConditional) {
    baseSourceHandleId = handlePairIdOrderByNode[sourceTaskId]?.[0];
    const sourceHandleIndex = getConditionalBranchIndex(targetTask, parentTask);
    const sourceHandleFormat = `source.${baseSourceHandleId}.${sourceHandleIndex}`;
    return { baseSourceHandleId, sourceHandleFormat };
  } else {
    // For multi-output tasks, map labware_id to the correct source handle
    const availableSourceHandles = handlePairIdOrderByNode[sourceTaskId] || [];
    let sourceHandleIndex = 0;

    if ('labware_sources' in sourceTask && sourceTask.labware_sources) {
      const sortedSourceLabwareKeys = Object.keys(
        sourceTask.labware_sources
      ).sort((a, b) => a.localeCompare(b));

      const matchingSourceKey = sortedSourceLabwareKeys.find((key) => {
        const sourceLs = sourceTask.labware_sources?.[key];
        return sourceLs?.labware_id === labwareId;
      });

      if (matchingSourceKey) {
        sourceHandleIndex = sortedSourceLabwareKeys.indexOf(matchingSourceKey);
      }
    }

    baseSourceHandleId =
      availableSourceHandles[sourceHandleIndex] || availableSourceHandles[0];
    const sourceHandleFormat = `source.${baseSourceHandleId}`;

    return { baseSourceHandleId, sourceHandleFormat };
  }
};
